import { z } from 'zod';
import { generateUUID } from '~/utils/uuid';

// ============================================================================
// Table Storage Types
// ============================================================================

/**
 * ID for the default uncategorized table.
 * Used when characters don't belong to a specific table.
 */
export const UNCATEGORIZED_TABLE_ID = '__uncategorized__';

/**
 * Zod schema for a table (group of characters).
 */
export const TableSchema = z.object({
  id: z.string().catch(generateUUID()),
  name: z.string().catch('Uncategorized'),
  activeCharacterId: z.string().optional(),
});

export type Table = z.infer<typeof TableSchema>;

// ============================================================================
// Character & Character Collection Storage Types
// ============================================================================

/**
 * Current version of the character data format.
 * Increment when making breaking changes to CharacterData structure.
 *
 * Version history:
 * - v1 (implicit): Original format with separate mind/body/reflex fields
 * - v2: Dynamic coreStats object, added version field
 * - v3: Added profile fields (age, appearance, relationships, notes)
 * - v4: Added belongsToTable field
 */
export const CHARACTER_DATA_VERSION = 4;

/**
 * Zod schema for a relationship entry in the character's profile.
 */
export const RelationshipSchema = z.object({
  id: z.string().catch(generateUUID()),
  name: z.string().catch(''),
  content: z.string().catch(''),
});

export type Relationship = z.infer<typeof RelationshipSchema>;

/**
 * Zod schema for character data with automatic defaults and validation.
 */
export const CharacterDataSchema = z.object({
  id: z.string().catch(generateUUID()),
  version: z.literal(CHARACTER_DATA_VERSION).catch(CHARACTER_DATA_VERSION),
  name: z.string().catch('New Adventurer'),
  coreStats: z.record(z.string(), z.number()).catch({ Mind: 2, Body: 3, Reflex: 2 }),
  currentHp: z.number().catch(9),
  currentResilience: z.number().catch(5),
  strain: z.number().catch(0),
  items: z.array(z.object({
    id: z.string().catch(''),
    content: z.string().catch(''),
  })).catch([]),
  appearance: z.string().catch(''),
  relationships: z.array(RelationshipSchema).catch([]),
  notes: z.string().catch(''),
  isMinimized: z.boolean().optional(),
  belongsToTable: z.string().optional(),
});

export type CharacterData = z.infer<typeof CharacterDataSchema>;

/**
 * CharacterCollection schema
 *
 * Note: This schema is primarily used during migration from the old localStorage format.
 * The new Zustand store uses its own versioning system via persist middleware.
 */
export const CharacterCollectionSchema = z.object({
  activeCharacterId: z.string().catch(''),
  characters: z.array(CharacterDataSchema).catch([]),
});

export type CharacterCollection = z.infer<typeof CharacterCollectionSchema>;

// ============================================================================
// Character & Character Collection Parsing with Migrations
// ============================================================================

/**
 * Try to parse and migrate character data. Returns null if invalid.
 */
export function tryParseCharacterData(data: unknown): CharacterData | null {
  if (!data || typeof data !== 'object') return null;

  const raw = data as Record<string, unknown>;
  const version = typeof raw.version === 'number' ? raw.version : 1;

  // Run migrations
  let migrated = { ...raw };

  if (version < 2) {
    // v1 -> v2: Convert mind/body/reflex to coreStats
    migrated = {
      ...migrated,
      version: 2,
      coreStats: {
        Mind: migrated.mind ?? 2,
        Body: migrated.body ?? 3,
        Reflex: migrated.reflex ?? 2,
      },
    };
    delete migrated.mind;
    delete migrated.body;
    delete migrated.reflex;
  }

  if (version < 3) {
    // v2 -> v3: Add profile fields
    migrated = {
      ...migrated,
      version: 3,
      appearance: migrated.appearance ?? '',
      relationships: migrated.relationships ?? [],
      notes: migrated.notes ?? '',
    };
  }

  if (version < 4) {
    // v3 -> v4: add belongsToTable field
    migrated = {
      ...migrated,
      version: 4,
      belongsToTable: migrated.belongsToTable ?? undefined,
    }
  }

  // Validate with Zod - provides defaults and type safety
  const result = CharacterDataSchema.safeParse(migrated);
  return result.success ? result.data : null;
}

/**
 * Try to parse and migrate character collection data.
 *
 * This function is primarily used during migration from the old localStorage format.
 * It validates the collection structure and migrates individual characters via tryParseCharacterData.
 *
 * Note: Collection-level versioning has been removed. Collection structure migrations
 * are now handled by Zustand's persist middleware. Character-level migrations continue
 * to use CHARACTER_DATA_VERSION.
 */
export function tryParseCharacterCollection(data: unknown): CharacterCollection | null {
  if (!data || typeof data !== 'object') return null;

  const raw = data as Record<string, unknown>;

  // Validate and migrate all characters (each character has its own version field)
  const characters = Array.isArray(raw.characters)
    ? raw.characters.map(tryParseCharacterData).filter((c): c is CharacterData => c !== null)
    : [];

  if (characters.length === 0) return null;

  // Ensure valid activeCharacterId
  let activeCharacterId = typeof raw.activeCharacterId === 'string' ? raw.activeCharacterId : '';
  if (!characters.find(c => c.id === activeCharacterId)) {
    activeCharacterId = characters[0].id;
  }

  // Validate with Zod - provides defaults and type safety
  const result = CharacterCollectionSchema.safeParse({
    activeCharacterId,
    characters,
  });
  return result.success ? result.data : null;
}
